/**
 * The daily loop.
 *
 * One screen for the routine: prep before the session, a checklist, a review
 * after, and a heatmap showing whether it was kept.
 *
 * The heatmap's colour vocabulary is deliberately not a gradient from bad to
 * good. A quiet day is drawn as absent rather than as a failure, because a
 * calendar that shades a holiday the same as a skipped session teaches the
 * trader to distrust it — and the fastest way to protect a streak would be to
 * never take a day off.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { JSX } from 'react';
import type {
  ChecklistResponse, ConsistencyResponse, NotebookEntryDto, StatisticsResponse, TemplateDto,
} from '@aegis/api-client';
import { api, fmtNum, fmtPct, fmtUsd } from '../lib/api.js';
import { EmptyState } from '../components/EmptyState.js';
import { Pnl } from '../components/panels.js';

const today = (): string => new Date().toISOString().slice(0, 10);

const OUTCOME_LABEL: Record<string, string> = {
  kept: 'Routine kept',
  partial: 'Part of the routine',
  skipped: 'Traded, no routine',
  'stood-aside': 'Stood aside deliberately',
  quiet: 'Nothing expected',
};

export function DailyScreen(): JSX.Element {
  const [day, setDay] = useState(today);
  const [entries, setEntries] = useState<NotebookEntryDto[]>([]);
  const [templates, setTemplates] = useState<TemplateDto[]>([]);
  const [checklist, setChecklist] = useState<ChecklistResponse | null>(null);
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [con, setCon] = useState<ConsistencyResponse | null>(null);
  /**
   * The day's own figures, for the header inside the review note.
   *
   * Same endpoint and same filter as the Reports screen, so the numbers cannot
   * disagree with it. Null while loading and null on failure — a review note
   * that refuses to open because its statistics did not load would be a worse
   * product than one that opens without them.
   */
  const [dayStats, setDayStats] = useState<StatisticsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [e, t, c, k] = await Promise.all([
        api.dailyEntries(day, day), api.templates(), api.checklist(), api.consistency(90),
      ]);
      setEntries(e.entries); setTemplates(t.templates);
      setChecklist(c); setCon(k); setError(null);

      // Separately, and allowed to fail on its own: the note is the thing the
      // user came for, and its figures are a garnish on it.
      void api.statistics({ from: day, to: day })
        .then(setDayStats)
        .catch(() => setDayStats(null));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load the day.');
    }
  }, [day]);

  useEffect(() => { void load(); }, [load]);

  const entryOf = (kind: 'prep' | 'review'): NotebookEntryDto | undefined =>
    entries.find((e) => e.kind === kind);

  const write = async (kind: 'prep' | 'review', body: string): Promise<void> => {
    setBusy(true);
    try {
      const existing = entryOf(kind);
      if (existing) await api.updateEntry(existing.id, { body });
      else await api.createEntry({ day, kind, body });
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save.');
    } finally { setBusy(false); }
  };

  const toggle = async (id: string): Promise<void> => {
    const next = new Set(checked);
    if (next.has(id)) next.delete(id); else next.add(id);
    setChecked(next);
    try { await api.runChecklist(day, [...next]); await load(); }
    catch { /* the tick stays optimistic; the next load reconciles it */ }
  };

  const standAside = async (): Promise<void> => {
    const reason = window.prompt('Why are you standing aside today?') ?? '';
    setBusy(true);
    try { await api.standAside(day, reason); await load(); }
    finally { setBusy(false); }
  };

  return (
    <div className="scroll-area">
      <div className="section-head">
        <h2>Daily</h2>
        <div className="account-picker">
          <input type="date" value={day} max={today()} className="select"
            onChange={(e) => setDay(e.target.value)} aria-label="Day" />
          <button className="btn btn-quiet compact" onClick={() => void standAside()} disabled={busy}>
            Stand aside today
          </button>
        </div>
      </div>

      {error && <div className="notice error">{error}</div>}

      <div className="daily-grid">
        <NoteCard
          title="Before the session" kind="prep" entry={entryOf('prep')}
          templates={templates.filter((t) => t.kind === 'prep')}
          busy={busy} onSave={(b) => void write('prep', b)}
        />
        <ChecklistCard
          checklist={checklist} checked={checked} busy={busy}
          onToggle={(id) => void toggle(id)}
          onSeed={async (items) => {
            setBusy(true);
            try { setChecklist(await api.saveChecklist(items.map((label) => ({ label })))); }
            finally { setBusy(false); }
          }}
        />
        <NoteCard
          title="After the session" kind="review" entry={entryOf('review')}
          templates={templates.filter((t) => t.kind === 'review')}
          busy={busy} onSave={(b) => void write('review', b)}
          // Only the review note. A prep note written before the session has no
          // session to report on, and putting yesterday's numbers above the
          // cursor would be answering a different day's question.
          figures={dayStats ? <DayFigures stats={dayStats} /> : null}
        />
      </div>

      {con && <Consistency c={con} />}
    </div>
  );
}

/**
 * The day's numbers, rendered INSIDE the note and above the cursor.
 *
 * `docs/23` §3.6. The point is not that the figures are available — they were
 * already on this screen and on Analytics. The point is where they sit: you
 * cannot type "solid day" over the top of `Profit factor 0.87`. Beside the
 * writing area they are reference; above the cursor they are the prompt.
 *
 * Computed, never typed, and not editable. Same endpoint as the Reports
 * screen's block, so a trader who checks one against the other finds them
 * equal.
 */
function DayFigures({ stats }: { stats: StatisticsResponse }): JSX.Element {
  const s = stats.summary;

  if (s.tradeCount === 0) {
    return (
      <div className="dayfigures dayfigures-empty">
        No trades recorded for this day.
      </div>
    );
  }

  const cells: readonly (readonly [string, string])[] = [
    ['Trades', String(s.tradeCount)],
    ['Winners', String(s.winCount)],
    ['Losers', String(s.lossCount)],
    ['Breakeven', String(s.scratchCount)],
    ['Win rate', fmtPct(s.winRate)],
    ['Gross', fmtUsd(s.grossPnl)],
    ['Commissions', fmtUsd(s.commissions)],
    ['Net', fmtUsd(s.netPnl)],
    ['Profit factor', s.profitFactor ? fmtNum(s.profitFactor, 2) : '—'],
    ['Volume', fmtNum(stats.deep.totalVolume, 0)],
  ];

  return (
    <dl className="dayfigures">
      {cells.map(([label, value]) => (
        <div key={label}>
          <dt>{label}</dt>
          {/* Only Net is toned. Every figure in colour would make the block a
              traffic light and stop any one of them standing out. */}
          <dd className="mono">
            {label === 'Net' ? <Pnl value={s.netPnl} /> : value}
          </dd>
        </div>
      ))}
    </dl>
  );
}

function NoteCard(
  { title, kind, entry, templates, busy, onSave, figures = null }: {
    title: string; kind: 'prep' | 'review'; entry: NotebookEntryDto | undefined;
    templates: TemplateDto[]; busy: boolean; onSave: (body: string) => void;
    /** Rendered above the writing area. See `DayFigures`. */
    figures?: JSX.Element | null;
  },
): JSX.Element {
  const [body, setBody] = useState(entry?.body ?? '');
  const [dirty, setDirty] = useState(false);
  useEffect(() => { setBody(entry?.body ?? ''); setDirty(false); }, [entry?.id, entry?.body]);

  return (
    <section className="panel daily-card">
      <header className="daily-card-head">
        <h3>{title}</h3>
        {templates.length > 0 && !body && (
          <select className="select compact" defaultValue=""
            aria-label={`Start ${kind} from a template`}
            onChange={(e) => {
              const t = templates.find((x) => x.id === e.target.value);
              if (t) { setBody(t.body); setDirty(true); }
            }}>
            <option value="" disabled>Start from a template…</option>
            {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        )}
      </header>
      {figures}
      <textarea
        value={body} rows={10} className="daily-text"
        placeholder={kind === 'prep'
          ? 'What are you looking for today, and what would make you stand aside?'
          : 'What actually happened, and which trades followed the plan?'}
        onChange={(e) => { setBody(e.target.value); setDirty(true); }}
      />
      <div className="form-actions">
        <button className="btn btn-primary compact" disabled={!dirty || busy}
          onClick={() => { onSave(body); setDirty(false); }}>
          {busy ? 'Saving…' : 'Save'}
        </button>
      </div>
    </section>
  );
}

function ChecklistCard(
  { checklist, checked, busy, onToggle, onSeed }: {
    checklist: ChecklistResponse | null; checked: Set<string>; busy: boolean;
    onToggle: (id: string) => void; onSeed: (items: string[]) => Promise<void>;
  },
): JSX.Element {
  if (!checklist) return <section className="panel daily-card"><p className="dim">Loading…</p></section>;

  if (checklist.items.length === 0) {
    return (
      <section className="panel daily-card">
        <h3>Checklist</h3>
        {/* Offered, never inserted. A checklist somebody else wrote is one the
            trader ticks without reading. */}
        <p className="dim">
          You have no checklist yet. These are a starting point — edit or replace them.
        </p>
        <ul className="checklist suggestions">
          {checklist.suggestions.map((s) => <li key={s}>{s}</li>)}
        </ul>
        <div className="form-actions">
          <button className="btn btn-primary compact" disabled={busy}
            onClick={() => void onSeed(checklist.suggestions)}>
            Use these
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="panel daily-card">
      <h3>Checklist</h3>
      <ul className="checklist">
        {checklist.items.map((i) => (
          <li key={i.id}>
            <label>
              <input type="checkbox" checked={checked.has(i.id)}
                onChange={() => onToggle(i.id)} disabled={busy} />
              <span>{i.label}</span>
            </label>
          </li>
        ))}
      </ul>
    </section>
  );
}

function Consistency({ c }: { c: ConsistencyResponse }): JSX.Element {
  const weeks = useMemo(() => {
    const out: (typeof c.days[number] | null)[][] = [];
    let week: (typeof c.days[number] | null)[] = [];
    for (const d of c.days) {
      const dow = new Date(`${d.day}T00:00:00Z`).getUTCDay();
      if (week.length === 0 && out.length === 0) for (let k = 0; k < dow; k += 1) week.push(null);
      week.push(d);
      if (week.length === 7) { out.push(week); week = []; }
    }
    if (week.length) out.push(week);
    return out;
  }, [c.days]);

  return (
    <section className="panel consistency">
      <header>
        <h3>Consistency</h3>
        <div className="consistency-stats">
          <span><strong>{(Number(c.adherence) * 100).toFixed(0)}%</strong> kept</span>
          <span><strong>{c.currentStreak}</strong> day streak</span>
          <span className="dim">best {c.longestStreak}</span>
        </div>
      </header>

      <div className="heatmap" role="img"
           aria-label={`Routine kept on ${(Number(c.adherence) * 100).toFixed(0)} percent of judged days over the last ${c.days.length}`}>
        {weeks.map((w, wi) => (
          <div className="heat-week" key={wi}>
            {w.map((d, di) => (
              <span key={d?.day ?? `${wi}-${di}`}
                className={`heat ${d ? d.outcome : 'pad'}`}
                title={d ? `${d.day} — ${OUTCOME_LABEL[d.outcome]}${d.tradeCount ? ` · ${d.tradeCount} trades` : ''}` : ''} />
            ))}
          </div>
        ))}
      </div>

      <ul className="heat-key">
        {(['kept', 'partial', 'skipped', 'stood-aside', 'quiet'] as const).map((o) => (
          <li key={o}><span className={`heat ${o}`} /> {OUTCOME_LABEL[o]}</li>
        ))}
      </ul>

      {c.quietDays > 0 && (
        <p className="dim small">
          {c.quietDays} day{c.quietDays === 1 ? '' : 's'} had nothing expected of them — no
          trades and no routine. They are excluded from the percentage rather than counted
          against it, so a week away does not cost you anything.
        </p>
      )}
    </section>
  );
}
